import { useState, useEffect, useRef } from "react";
import ProductHeader from "../../Components/ProductsPages/ProductHeader";
import ProductsHeroSection from "../../Components/ProductsPages/ProductsHeroSection";
import ProductFooter from "../../Components/ProductsPages/ProductFooter";
import RequestDemo from "../../Components/ProductsPages/RequestDemo";
import QSleeveImage from "../../Images/videothumbnail.jpg";
import BackgroundGradient from "../../Images/BackgroundGradient.png";
import BackgroundGradientSmall from "../../Images/BackgroundGradientSmall.png";
import VideoComponent from "../../Components/VideoComponent";
import { KeyFeatures } from "../../Components/KeyFeatures";
import {
  ProductTable,
  IOTable,
} from "../../Components/ProductsPages/ProductTable";
import QUBITIQ_P1 from "../../Images/Boxes/QUBITIQ_P1.jpg";

export default function QubitIQ() {
  const requestDemoRef = useRef(null);
  const architectureRef = useRef(null);

  const [backgroundImage, setBackgroundImage] = useState(``);

  useEffect(() => {
    const updateBackgroundImage = () => {
      if (window.innerWidth < 640) {
        setBackgroundImage(`url(${BackgroundGradientSmall})`);
      } else {
        setBackgroundImage(`url(${BackgroundGradient})`);
      }
    };

    updateBackgroundImage();

    window.addEventListener("resize", updateBackgroundImage);

    return () => window.removeEventListener("resize", updateBackgroundImage);
  }, []);

  const scrollToRequestDemo = () => {
    requestDemoRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToArchitecture = () => {
    architectureRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const keyFeatures = [
    "AI-Driven Quantum Intelligence Leverages machine learning to recommend optimal encodings, circuits, and algorithms.",
    "End-to-End Quantum Workflow Covers everything from problem mapping to execution on simulators and QPUs.",
    "Real-Time Optimization &amp; Benchmarking Dynamically evaluates noise, circuit depth, and hardware constraints.",
    "Hardware-Aware Execution Ensures efficient deployment across CPUs, GPUs, and quantum hardware.",
    "Scalable &amp; Problem-Agnostic Adapts to diverse domains including finance, pharma, logistics, and energy.",
    "Enterprise-Ready Platform Simplifies quantum adoption with a unified and intelligent ecosystem.",

    "First AI-Driven Quantum Development Ecosystem Integrates algorithm generation, VQA benchmarking, noise analysis, and optimization in one platform.",
    "ML-Guided Encoding &amp; Circuit Generation Accelerates algorithm design by intelligently mapping problems to quantum /representations.",
    "Integrated Noise Analysis &amp; Mitigation Suggests techniques to improve circuit fidelity and execution reliability.",
    "Quantum Resource Estimation Provides insights into qubit requirements, gate depth, and hardware feasibility.",
    "Adaptive Knowledge Base Continuously learns from quantum workflows to improve recommendations.",
    "Cross-Platform Execution Capability Enables seamless deployment on simulators, GPUs, and real QPUs.",

    "Input Problem Statement The user provides a computational problem or dataset.",
    "AI-Based Mapping The system maps problems to suitable quantum algorithms and encodings.",
    "Quantum Circuit Generation Generates optimized circuits using ML-driven techniques.",
    "Optimization Layer Performs circuit optimization, noise analysis, and mitigation suggestions.",
    "Execution Layer Runs on: 1. Quantum simulators (CPU/GPU) 2. Real quantum hardware (QPU)",
    "Output & Insights Provides optimized circuits, pseudocode, and performance analysis.",

    "Intelligent recommendations instead of manual design",
    "Built-in benchmarking and optimization",
    "Hardware-aware execution strategies",
    "Complete lifecycle coverage from idea to implementation",

    "Quantum Algorithm Development",
    "Variational Quantum Algorithms (VQA)",
    "Quantum Machine Learning",
    "Optimization Problems",
    "Financial Modeling",
    "Drug Discovery &amp; Material Science",
    "Logistics &amp; Supply Chain Optimization",
  ];

  const specifications = [
    ["Feature", "Specification"],
    [
      "Circuit Optimization",
      "Automated circuit optimization with depth and gate efficiency improvements",
    ],
    [
      "Noise Analysis",
      "Built-in noise evaluation and mitigation technique recommendations",
    ],
    [
      "Benchmarking",
      "Real-time performance benchmarking across circuits and hardware",
    ],
    [
      "Resource Estimation",
      "Qubit count, gate depth, and hardware requirement estimation",
    ],
    [
      "Hardware Compatibility",
      "Hardware-agnostic (compatible with multiple quantum computing platforms)",
    ],
    [
      "Output Formats",
      "Optimized Quantum Circuits, Pseudocode, Performance Analysis Reports",
    ],
    [
      "Target Users",
      "Researchers, Enterprises, Quantum Developers, Academic Institutions",
    ],
  ];

  return (
    <div className="w-full  flex flex-col gap-0 items-center justify-center bg-primary7">
      <div
        className="w-full width flex flex-col gap-0 items-center justify-center bg-contain bg-repeat"
        style={{ backgroundImage: backgroundImage }}
      >
        <ProductHeader scrollToRequestDemo={scrollToRequestDemo} />
        <div className="w-full h-[25rem] ">
          <ProductsHeroSection
            title1="Reimagine Computing"
            title2="With Quantum Intelligence"
            content="Unify classical, quantum, and hybrid paradigms into one intelligent ecosystem that accelerates discovery, innovation, and enterprise adoption. "
            scrollToArchitecture={scrollToArchitecture}
          />
        </div>

        <div className="w-full bg-primary7 width md:px-10  flex flex-col items-center justify-center pb-10 gap-1">
          <div className="w-[90%] text-center md:text-left text-[1.5rem] md:text-[2rem] 2xl:text-[2.3rem] font-RobotoBold text-white leading-tight px-3 ">
            <span className="wordGradient">Quantum Inteliigence</span>
          </div>

          <div className="w-[90%] h-full   grid grid-cols-1 xl:grid-cols-2  place-items-start  p-3">
            <div className="w-full  flex flex-col items-center justify-center  gap-1">
              <div className="w-full text-white text-left font-RobotoRegular text-xs md:text-sm px-3">
                QubitIQ is an intelligent, unified platform designed to
                accelerate quantum algorithm development by seamlessly
                integrating classical, quantum, and hybrid computing paradigms.
                Powered by AI-driven recommendations, it enables efficient data
                encoding, circuit optimization, and hardware-aware execution -
                making quantum computing more accessible, scalable, and
                enterprise-ready. From experimentation to deployment, QubitIQ
                simplifies the entire quantum workflow, empowering organizations
                to unlock real-world impact across industries.
              </div>

              <div className="w-full px-3 flex items-start justify-start">
                <KeyFeatures
                  title="Highlights"
                  features={keyFeatures.slice(0, 7)}
                />
              </div>
            </div>

            <div className="w-full  flex flex-col items-center justify-center  gap-1">
              <KeyFeatures
                title="Innovation"
                features={keyFeatures.slice(7, 12)}
              />
              <div className="w-full  flex flex-col items-center justify-center  gap-1">
                <h3 className="w-full wordGradient font-semibold text-[1.5rem] md:text-[2rem] 2xl:text-[2.3rem] text-left mb-2">
                  Uniqueness
                </h3>
                <div className="w-full text-white text-left font-RobotoRegular text-xs md:text-sm px-3">
                  QubitIQ stands apart as a fully integrated, AI-powered quantum
                  intelligence platform that goes beyond traditional SDKs like
                  Qiskit or PennyLane by offering:
                </div>

                <div className="w-full px-3 flex items-start justify-start">
                  <KeyFeatures features={keyFeatures.slice(18, 22)} />
                </div>
              </div>
            </div>
          </div>
          <div className="w-[90%] h-full  grid grid-cols-1 xl:grid-cols-2  place-items-start  ">
            <KeyFeatures
              title="Working Mechanism"
              features={keyFeatures.slice(12, 18)}
            />

            <KeyFeatures
              title="Applications"
              features={keyFeatures.slice(22, 29)}
            />
          </div>
          <div className="w-[90%] h-full flex flex-col md:flex-row justify-center items-center gap-10 my-10">
            <div className="w-full flex flex-col items-center justify-center">
              <img
                className="w-[21.5rem]  lg:w-[55.5rem] md:w-[600px]  object-contain rounded-lg md:rounded-2xl"
                src={QUBITIQ_P1}
              ></img>
              <p className="w-full lg:max-w-[600px]  text-center text-xs italic text-white/70 mt-2">
                Figure 1: Qubit Intelligence” built upon “Qubit-IQ” enables
                organizations with AI-driven Quantum Intelligence, accelerating
                optimal Quantum Algorithm development at scale
              </p>
            </div>
          </div>
          <ProductTable title="Specifications" data={specifications} />
        </div>

        {/* <VideoComponent
          image={QSleeveImage}
          video={"https://thequantum.ai/videos/QSleeveVideo.mp4"}
        /> */}
        <div
          className="w-full bg-primary7 width flex flex-col items-center justify-center gap-2 "
          ref={requestDemoRef}
        >
          <RequestDemo />
        </div>

        <ProductFooter />
      </div>
    </div>
  );
}
